'use client';

/**
 * useTosAcceptance — H3 (Audit) — Terms-of-Service version-gate hook.
 *
 * Compares the user's most-recently-accepted ToS version against
 * `CURRENT_TOS_VERSION` from `lib/legal/terms-versions.ts`. When the
 * accepted version is older (or there is no acceptance row at all),
 * `<TosAcceptanceModal>` should be opened with a non-dismissible
 * "Accept new Terms" CTA.
 *
 * Backed by `useApiQuery` so the fetch + StrictMode + abort handling is
 * shared with the rest of the codebase, and so we do not trip the
 * `react-hooks/set-state-in-effect` lint rule.
 */

import { useCallback, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useApiQuery } from '@/hooks/useApiQuery';
import { fetchWithAuth } from '@/lib/utils/api-auth';
import { CURRENT_TOS_VERSION } from '@/lib/legal/terms-versions';

type TosMeResponse = {
  success?: boolean;
  data?: {
    acceptedVersion?: string | null;
    acceptedAt?: string | null;
  };
};

type TosMePayload = {
  acceptedVersion: string | null;
  acceptedAt: string | null;
};

export type UseTosAcceptanceResult = {
  /** True only after the API has returned and the version is older than the live one. */
  needsAcceptance: boolean;
  /** The ToS version the user previously accepted (if any). */
  acceptedVersion: string | null;
  /** The version the user is being asked to accept now. */
  currentVersion: string;
  /** Whether the initial fetch has completed at least once. */
  hydrated: boolean;
  /** POST `/api/tos/accept` with the current version. */
  accept: () => Promise<{ ok: boolean; error?: string }>;
  /** Manually re-fetch the latest acceptance row (e.g. after sign-in). */
  refresh: () => Promise<void>;
};

async function fetchTosMe(signal: AbortSignal): Promise<TosMePayload> {
  const response = await fetchWithAuth('/api/tos/me', { method: 'GET', silent: true, signal });
  if (!response.ok) {
    // 401/5xx — return a sentinel that does NOT trip the modal so the
    // gate stays hidden during a transient unauthenticated race.
    return { acceptedVersion: CURRENT_TOS_VERSION, acceptedAt: null };
  }
  const json = (await response.json()) as TosMeResponse;
  const v = json.data?.acceptedVersion;
  const a = json.data?.acceptedAt;
  return {
    acceptedVersion: typeof v === 'string' ? v : null,
    acceptedAt: typeof a === 'string' ? a : null,
  };
}

export function useTosAcceptance(): UseTosAcceptanceResult {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [optimisticVersion, setOptimisticVersion] = useState<string | null>(null);

  const enabled = !authLoading && isAuthenticated;

  const { data, isLoading, refetch } = useApiQuery<TosMePayload>(
    ['tos-me', isAuthenticated ? '1' : '0'],
    (signal) => fetchTosMe(signal),
    {
      enabled,
      // The hook is non-critical; failures are silent because the user
      // will get a chance to accept after the next page-refresh anyway.
      onError: () => {},
    },
  );

  const refresh = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const accept = useCallback(async () => {
    try {
      const response = await fetchWithAuth('/api/tos/accept', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ version: CURRENT_TOS_VERSION }),
      });
      if (!response.ok) {
        const fallback = `tos_accept_failed_${response.status}`;
        try {
          const json = (await response.json()) as { error?: string };
          return { ok: false, error: json.error ?? fallback };
        } catch {
          return { ok: false, error: fallback };
        }
      }
      // Optimistic update — the next refetch will reconcile.
      setOptimisticVersion(CURRENT_TOS_VERSION);
      void refetch();
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err instanceof Error ? err.message : 'tos_accept_unknown' };
    }
  }, [refetch]);

  // The optimistic version takes precedence so the modal disappears
  // immediately on Accept; the next refetch reconciles to the real row.
  const acceptedVersion = optimisticVersion ?? data?.acceptedVersion ?? null;
  const hydrated = !authLoading && (!enabled || (!isLoading && data !== undefined));
  const needsAcceptance =
    isAuthenticated && hydrated && (acceptedVersion === null || acceptedVersion !== CURRENT_TOS_VERSION);

  return {
    needsAcceptance,
    acceptedVersion,
    currentVersion: CURRENT_TOS_VERSION,
    hydrated,
    accept,
    refresh,
  };
}
